import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, CalendarClock, Search, MapPin, PhoneCall } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { useMeta, injectJsonLd, removeJsonLd } from "../lib/seo";
import { HOME_FAQS, ORG_SCHEMA, WEBSITE_SCHEMA, faqJsonLd } from "../seoShared";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { track } from "../lib/analytics";
import { StagePicker } from "../components/StagePicker";
import { PRICE_COPY } from "../config/pricing";

// Homepage: resource-first. The free guides lead; the paid letter is the alternative, not the hook.
const GUIDE_COLUMNS = [
  { axis: "situation", labelKey: "home2.guidesColSituation" },
  { axis: "write-it-yourself", labelKey: "home2.guidesColWrite" },
  { axis: "reference", labelKey: "home2.guidesColReference" },
];

const TOOLS = [
  { to: "/guide/tools#deadline", Icon: CalendarClock, titleKey: "home2.toolDeadline", testId: "tool-deadline" },
  { to: "/guide/tools#reasons", Icon: Search, titleKey: "home2.toolReasons", testId: "tool-reasons" },
  { to: "/guide/tools#regulators", Icon: MapPin, titleKey: "home2.toolRegulators", testId: "tool-regulators" },
  { to: "/guide/tools#call", Icon: PhoneCall, titleKey: "home2.toolCall", testId: "tool-call" },
];

export default function Landing() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  useMeta("Denied Warranty Claim? Free Denial Check | Denial Decoder",
    "Upload your warranty or service contract denial letter for a free verdict in minutes. Add your contract for the exact-clause read and appeal letter.");
  const start = (location = "unknown") => { track("cta_click", { location }); track("start_check"); navigate("/check"); };
  const [axes, setAxes] = useState(null);
  useEffect(() => { api.get("/guide/pages").then((r) => setAxes(r.data.axes || {})).catch(() => setAxes({})); }, []);
  useEffect(() => {
    injectJsonLd("org-schema", ORG_SCHEMA);
    injectJsonLd("website-schema", WEBSITE_SCHEMA);
    injectJsonLd("faq-schema", faqJsonLd(HOME_FAQS));
    return () => { removeJsonLd("org-schema"); removeJsonLd("website-schema"); removeJsonLd("faq-schema"); };
  }, []);
  const allGuides = axes ? Object.values(axes).flat() : [];
  const spotGuide = allGuides.find((g) => g.slug === "how-to-read-your-contract")
    || (axes?.reference || [])[0];

  return (
    <div>
      {/* 2. Hero — guides are the primary action */}
      <section className="relative overflow-hidden" style={{ background: "linear-gradient(135deg, #f8fafc 0%, #f0fdf4 50%, #ecfdf5 100%)" }} data-testid="hero-section">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="hero-grid" width="44" height="44" patternUnits="userSpaceOnUse">
                <path d="M 44 0 L 0 0 0 44" fill="none" stroke="#059669" strokeOpacity="0.07" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#hero-grid)" />
          </svg>
          <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full" style={{ background: "#34d399", opacity: 0.12 }} />
          <div className="absolute -bottom-48 right-10 w-80 h-80 rounded-full" style={{ background: "#34d399", opacity: 0.10 }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className="max-w-3xl fade-in">
            <p className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: "#047857" }} data-testid="hero-eyebrow">{t("home2.eyebrow")}</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1]" style={{ color: "#0f172a" }}>
              {t("home2.h1")}
            </h1>
            <p className="mt-6 text-lg leading-relaxed" style={{ color: "#475569" }}>
              {t("home2.sub")}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Button size="lg" asChild className="text-white rounded-full px-8 text-base bg-emerald-700 hover:bg-emerald-800 w-full sm:w-auto" data-testid="read-guides-hero-cta">
                <Link to="/guide" onClick={() => track("cta_click", { location: "hero_guides" })}>{t("home2.ctaGuides")}</Link>
              </Button>
              <Button size="lg" variant="outline" onClick={() => start("hero")} className="rounded-full px-8 text-base border-emerald-700 text-emerald-700 hover:bg-emerald-50 w-full sm:w-auto" data-testid="check-denial-free-hero-cta">
                {t("home2.ctaCheck")}
              </Button>
            </div>
            <p className="mt-4 text-xs" style={{ color: "#64748b" }} data-testid="hero-note">{t("home2.heroNote")}</p>
          </div>
        </div>
      </section>

      {/* 3. The guides — live data, grouped by the guide index's axes; empty columns hide */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20" data-testid="guides-section">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("home2.guidesTitle")}</h2>
            <p className="mt-2 text-base text-slate-600">{t("home2.guidesSub")}</p>
          </div>
          <Link to="/guide" className="text-emerald-700 font-semibold text-sm hover:underline inline-flex items-center gap-1 min-h-[44px]" data-testid="guides-see-all">
            {allGuides.length ? t("home2.guidesSeeAllCount", { count: allGuides.length }) : t("home2.guidesSeeAll")}
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {GUIDE_COLUMNS.map(({ axis, labelKey }) => {
            const pages = axes?.[axis] || [];
            if (axes && !pages.length) return null;
            return (
              <div key={axis} className="bg-white border border-slate-200 rounded-2xl p-6" data-testid={`guides-col-${axis}`}>
                <p className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-4">{t(labelKey)}</p>
                <div className="space-y-3">
                  {pages.map((g) => (
                    <Link key={g.slug} to={`/guide/${g.slug}`} data-testid={`guide-link-${g.slug}`}
                      className="block text-sm font-medium text-slate-800 hover:text-emerald-700 leading-snug py-1">
                      {g.title}
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* 4. Free tools */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="free-tools-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
          <div className="flex items-baseline gap-3 flex-wrap mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("home2.toolsTitle")}</h2>
            <p className="text-sm text-slate-500">{t("home2.toolsSub")}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {TOOLS.map((tool) => (
              <Link key={tool.testId} to={tool.to} data-testid={`home-${tool.testId}`}
                className="bg-white border border-slate-200 rounded-2xl p-5 hover:border-emerald-400 hover:shadow-md transition-shadow flex items-center gap-3 min-h-[44px]">
                <tool.Icon size={20} className="text-emerald-700 shrink-0" aria-hidden="true" />
                <span className="font-semibold text-sm text-slate-900">{t(tool.titleKey)}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 5. What reading a denial properly looks like — ends in a guide link, not a buy button */}
      <section className="bg-slate-900" data-testid="sample-analysis-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">{t("home2.sampleTitle")}</h2>
          <p className="mt-3 text-base text-slate-300 max-w-2xl">{t("home2.sampleSub")}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
            <div className="bg-white rounded-2xl p-6" data-testid="sample-denial-card">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">{t("home2.sampleDenialLabel")}</p>
              <p className="text-slate-900 leading-relaxed">
                {t("home2.sampleDenialPre")}{" "}<mark className="bg-yellow-200 text-slate-900 rounded px-0.5">{t("home2.sampleDenialMark")}</mark>{t("home2.sampleDenialPost")}
              </p>
            </div>
            <div className="bg-white rounded-2xl p-6" data-testid="sample-clause-card">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">{t("home2.sampleClauseLabel")}</p>
              <p className="text-slate-900 leading-relaxed">
                {t("home2.sampleClausePre")}{" "}<mark className="bg-yellow-200 text-slate-900 rounded px-0.5">{t("home2.sampleClauseMark")}</mark>{" "}{t("home2.sampleClausePost")}
              </p>
            </div>
          </div>
          <p className="mt-8 text-sm text-slate-300" data-testid="sample-bottom-line">
            {t("home2.sampleBottom")}{" "}
            <Link to={spotGuide ? `/guide/${spotGuide.slug}` : "/guide"} className="text-emerald-300 font-semibold hover:underline inline-flex items-center gap-1" data-testid="sample-guide-link">
              {t("home2.sampleLink")}
            </Link>
          </p>
        </div>
      </section>

      {/* 6. Or we'll do it for you — the paid path */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20" data-testid="paid-path-section">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("home2.paidTitle")}</h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">{t("home2.paidIntro")}</p>
            <ol className="mt-8 space-y-5">
              {[1, 2, 3].map((n) => (
                <li key={n} className="flex gap-4" data-testid={`paid-step-${n}`}>
                  <span className="bg-emerald-700 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0" aria-hidden="true">{n}</span>
                  <p className="text-base text-slate-700 pt-1">{t(`home2.paidStep${n}`)}</p>
                </li>
              ))}
            </ol>
          </div>
          <div className="bg-white border border-slate-200 rounded-3xl p-8" data-testid="paid-price-card">
            <p className="text-4xl font-extrabold tracking-tight text-slate-900">
              {PRICE_COPY.letter} <span className="text-base font-semibold text-slate-500">{t("home2.paidPerLetter")}</span>
            </p>
            <p className="mt-3 text-sm text-slate-600 leading-relaxed">
              {t("home2.paidMail", { mail: PRICE_COPY.letterPlusMail })}
            </p>
            <Button size="lg" onClick={() => start("pricing-card")} className="w-full mt-6 text-white rounded-full bg-emerald-700 hover:bg-emerald-800 min-h-[44px]" data-testid="start-free-check-cta">
              {t("home2.paidCta")}
            </Button>
            <p className="mt-3 text-xs text-slate-500 text-center">{t("home2.paidNote")}</p>
          </div>
        </div>
      </section>

      {/* 7. Where are you right now — unchanged component, moved */}
      <StagePicker />

      {/* 8. What we don't do */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="what-we-dont-do-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-8">{t("home2.dontTitle")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((n) => (
              <p key={n} className="text-sm text-slate-600 leading-relaxed flex gap-2" data-testid={`dont-do-${n}`}>
                <CheckCircle2 size={16} className="text-emerald-700 shrink-0 mt-0.5" aria-hidden="true" />
                {t(`home2.dont${n}`)}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* 9. FAQ — existing section, new first question (faq1) */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20" data-testid="home-faq-section">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-6">{t("landing.faqTitle")}</h2>
        <Accordion type="single" collapsible>
          {[1, 2, 3, 4, 5].map((n) => (
            <AccordionItem key={n} value={`hfaq-${n}`}>
              <AccordionTrigger data-testid={`home-faq-${n}`}>{t(`landing.faq${n}q`)}</AccordionTrigger>
              <AccordionContent>{t(`landing.faq${n}a`)}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </div>
  );
}
